/** @jsx jsx */
import {
  Box,
  Button,
  Divider,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerOverlay,
  Flex,
  Icon,
  Link,
  List,
  ListItem,
  Stack,
  useDisclosure,
} from '@chakra-ui/core'
import { css, jsx } from '@emotion/core'
import RouterLink from 'next/link'
import { useRouter } from 'next/router'
import { useRef } from 'react'
import {
  FiArrowRight,
  FiExternalLink,
  FiMenu,
  FiUser,
  FiX,
} from 'react-icons/fi'
import { MenuItems as MenuItemsType } from 'types'
import { useAuth } from 'use-auth0-hooks'
import GridContainer from './GridContainer'
import GridItem from './GridItem'
import QrCode from './QrCode'
import l10n from 'l10n'
import useUser from 'hooks/useUser'

const myPageServices = [
  {
    title: l10n.myPerks,
    intro: l10n.myPerksText,
    url: '/minside/fordeler',
  },
  {
    title: l10n.myPurchases,
    intro: l10n.myPurchasesText,
    url: '/minside/kjop',
  },
  {
    title: l10n.myFavourites,
    intro: l10n.myFavouritesText,
    url: '/minside/favoritter',
  },
  {
    title: l10n.myGym,
    intro: l10n.myGymText,
    url: '/minside/trening',
  },
  {
    title: l10n.myProfile,
    intro: l10n.myProfileText,
    url: '/minside/profil',
  },
  {
    title: l10n.myConsents,
    intro: l10n.myConsentsText,
    url: '/minside/samtykker',
  },
]

const MenuItemPageLink = ({ children, link, openInNewWindow, arrow }: any) => {
  return (
    <RouterLink href="/[...uri]" as={link.url} passHref>
      <Link color="red.900" fontWeight="bold" fontSize="md">
        {children}
        {openInNewWindow ? (
          <Box as={FiExternalLink} display="inline" mx={1} />
        ) : (
          arrow && (
            <Box as={FiArrowRight} color="red.600" display="inline" mx={1} />
          )
        )}
      </Link>
    </RouterLink>
  )
}

const MenuItemUrlLink = ({ children, item, arrow }: any) => {
  return (
    <Link
      isExternal={item.openInNewWindow}
      href={item.linkUrl}
      color="red.900"
      fontWeight="bold"
      fontSize="md"
    >
      {children}
      {item.openInNewWindow ? (
        <Box as={FiExternalLink} display="inline" mx={1} />
      ) : (
        arrow && (
          <Box as={FiArrowRight} color="red.600" display="inline" mx={1} />
        )
      )}
    </Link>
  )
}

const MenuItems = ({ menuItems, arrow = true }) => {
  if (!menuItems) return <></>

  return menuItems?.map((item) => {
    if (item.typeHandle === 'url') {
      return (
        <ListItem my={2} key={item.uid}>
          <MenuItemUrlLink arrow={arrow} item={item}>
            {item.linkTitle}
          </MenuItemUrlLink>
        </ListItem>
      )
    }
    return item?.pageLink?.map((link) => (
      <ListItem my={2} key={link.uid}>
        <MenuItemPageLink
          arrow={arrow}
          link={link}
          openInNewWindow={item.openInNewWindow}
        >
          {link.title}
        </MenuItemPageLink>
      </ListItem>
    ))
  })
}

const Header = ({
  colorTheme = 'light',
  headerBackgroundColor = 'white',
  services,
  menuItems,
  submenuItems,
}: Props) => {
  const { data } = useUser()
  const { user, logout } = useAuth()
  const router = useRouter()
  const { isOpen, onOpen, onClose } = useDisclosure()
  const btnRef = useRef()
  const closeBtn = useRef()

  return (
    <Box
      backgroundColor={headerBackgroundColor}
      position="fixed"
      top={0}
      width="100%"
      zIndex={1300}
      borderBottom={colorTheme === 'light' ? '1px solid' : null}
      borderBottomColor={colorTheme === 'light' ? 'gray.200' : null}
    >
      <Flex
        maxWidth="7xl"
        margin="auto"
        px={6}
        py={[4, 6, 8]}
        as="header"
        alignItems="center"
        justifyContent="space-between"
        color={colorTheme === 'light' ? 'gray.800' : 'white'}
      >
        <RouterLink href="/" as="/">
          <a>
            <Box
              as="img"
              // @ts-ignore
              src="/logos/mobile.svg"
              alt=""
              display={['block', null, null, 'none']}
            />
            {colorTheme == 'light' ? (
              <Box
                as="img"
                // @ts-ignore
                src="/logos/desktop.svg"
                alt=""
                display={['none', null, null, 'block']}
              />
            ) : (
              <Box
                as="img"
                // @ts-ignore
                src="/logos/darkDesktop.svg"
                alt=""
                display={['none', null, null, 'block']}
              />
            )}
          </a>
        </RouterLink>
        <Stack isInline spacing={[2, 2, 6]} alignItems="center">
          {menuItems?.map((item) => {
            if (item.typeHandle === 'url') {
              return (
                <Link
                  key={item.uid}
                  display={['none', null, 'block']}
                  isExternal={item.openInNewWindow}
                  href={item.linkUrl}
                  fontWeight="bold"
                  fontSize="md"
                >
                  {item.linkTitle}
                  {item.openInNewWindow && (
                    <Box as={FiExternalLink} display="inline" mx={1} />
                  )}
                </Link>
              )
            }
            return item?.pageLink?.map((link) => (
              <Flex display={['none', null, 'block']} key={item.uid}>
                <RouterLink href="/[...uri]" as={link.url} passHref>
                  <Link fontWeight="bold" fontSize="md">
                    {link.title}
                  </Link>
                </RouterLink>
              </Flex>
            ))
          })}
          <RouterLink href="/minside" as="/minside" passHref>
            <Button
              display={['none', 'inherit']}
              color={colorTheme === 'light' ? 'gray.800' : 'white'}
              variant="link"
              as="a"
              size="md"
              fontSize="md"
              mr={4}
              ml={[4, 12]}
              leftIcon={FiUser}
            >
              {l10n.myPage}
            </Button>
          </RouterLink>
          <Button
            size="md"
            variantColor="orange"
            backgroundColor="orange.400"
            color="red.900"
            onClick={onOpen}
            leftIcon={FiMenu}
            ref={btnRef}
          >
            {l10n.menu}
          </Button>
        </Stack>

        <Drawer
          isOpen={isOpen}
          placement="right"
          onClose={onClose}
          finalFocusRef={btnRef}
          initialFocusRef={closeBtn}
          size="full"
        >
          <DrawerOverlay />
          <DrawerContent backgroundColor="brown.50">
            <DrawerBody
              p={0}
              as={Flex}
              flexDirection="column"
              justifyContent="space-between"
              overflowY="scroll"
            >
              <Box>
                <Flex
                  maxWidth="7xl"
                  margin="0 auto"
                  width="100%"
                  alignItems="center"
                  justifyContent="space-between"
                  px={6}
                  py={[4, 6, 8]}
                >
                  <RouterLink href="/" as="/">
                    <a>
                      <img src="/logos/mobile.svg" alt="" />
                    </a>
                  </RouterLink>
                  <Stack isInline spacing={4}>
                    <RouterLink href="/minside" as="/minside" passHref>
                      <Button
                        variant="link"
                        color="black"
                        as="a"
                        leftIcon={FiUser}
                        ml={[0, 0, 3]}
                      >
                        {l10n.myPage}
                      </Button>
                    </RouterLink>
                    <Button
                      color="red.900"
                      variant="outline"
                      onClick={onClose}
                      leftIcon={FiX}
                      ml={[3, 3, 6]}
                      // pr={[1, 1, 4]}
                      ref={closeBtn}
                    >
                      {l10n.close}
                    </Button>
                  </Stack>
                </Flex>
                <Divider mb={[8, 8, 12]} />
                {user && (
                  <Box px={4} pb={8} display={[null, null, null, 'none']}>
                    <QrCode />
                  </Box>
                )}
                <GridContainer
                  alignItems="start"
                  // @ts-ignore
                  px={6}
                >
                  <GridItem colSpan={[12, 12, 12, 4]}>
                    <Box as="h2" color="red.700" mb={4}>
                      {l10n.ourServices}
                    </Box>
                    <List spacing={[4, 5]}>
                      {services?.map((service) => (
                        <ListItem key={service.uid}>
                          <RouterLink
                            href="/[...uri]"
                            as={service.url}
                            passHref
                          >
                            <Link
                              fontFamily="serif"
                              color="red.900"
                              fontWeight="bold"
                              fontSize={['lg', null, null, 'xl']}
                            >
                              <Box
                                // @ts-ignore
                                as="img"
                                // @ts-ignore
                                src={service.navigationalSymbol?.[0].url}
                                width={6}
                                height={6}
                                mr={4}
                                mt={['-2px', '-4px']}
                                color="red.600"
                                display="inline"
                                css={css`
                                  filter: brightness(0) saturate(100%)
                                    invert(19%) sepia(89%) saturate(2132%)
                                    hue-rotate(344deg) brightness(96%)
                                    contrast(91%);
                                `}
                              />
                              {service.title}
                            </Link>
                          </RouterLink>
                        </ListItem>
                      ))}
                    </List>
                  </GridItem>
                  <GridItem colSpan={[12, 12, 12, 4]} mt={[10, 10, 10, 0]}>
                    <Box as="h2" color="red.700" mb={4}>
                      {l10n.oftenUsedPages}
                    </Box>
                    <List>
                      <MenuItems menuItems={menuItems} />
                    </List>
                    <List my={12}>
                      <MenuItems menuItems={submenuItems} arrow={false} />
                    </List>
                  </GridItem>

                  <GridItem
                    colSpan={[12, 12, 12, 4]}
                    // @ts-ignore
                    // display={['none', 'none', 'none', 'block']}
                  >
                    {user ? (
                      <Box mb={12}>
                        <Box as="h2" color="red.600" mb={4}>
                          {l10n.myPage}
                        </Box>
                        <List
                          spacing={2}
                          color="red.900"
                          fontWeight="bold"
                          fontSize="md"
                        >
                          {myPageServices.map((item) => (
                            <ListItem key={item.url}>
                              <RouterLink
                                href={item.url}
                                as={item.url}
                                passHref
                              >
                                <Link>{item.title}</Link>
                              </RouterLink>
                            </ListItem>
                          ))}
                          <ListItem>
                            <Link
                              onClick={() =>
                                logout({
                                  returnTo:
                                    process.env
                                      .NEXT_PUBLIC_APP_AUTH0_LOGOUT_URI,
                                })
                              }
                            >
                              Logg ut
                            </Link>
                          </ListItem>
                        </List>
                        <Box mt={6} display={['none', 'none', 'none', 'block']}>
                          <QrCode />
                        </Box>
                      </Box>
                    ) : (
                      <Box backgroundColor="orange.50" p={8}>
                        <Box
                          as="h2"
                          color="orange.900"
                          fontWeight="bold"
                          fontSize="lg"
                        >
                          {l10n.logInAndReceivePerks1}
                        </Box>
                        <Box as="p" color="orange.900" my={6}>
                          {l10n.logInAndReceivePerks2}
                        </Box>
                        <Button
                          as={Link}
                          variantColor="orange"
                          backgroundColor="orange.400"
                          color="red.900"
                          mr={2}
                          //@ts-ignore
                          href="/minside/logginn"
                        >
                          {l10n.logIn}
                        </Button>
                        <Button
                          as={Link}
                          variantColor="white"
                          backgroundColor="white"
                          color="red.900"
                          //@ts-ignore
                          href="/minside/registrer"
                        >
                          {l10n.newUser}
                        </Button>
                      </Box>
                    )}
                  </GridItem>
                </GridContainer>
              </Box>
              <Box backgroundColor="brown.100">
                <Flex
                  maxWidth="7xl"
                  margin="0 auto"
                  width="100%"
                  justifyContent="space-between"
                  fontSize="lg"
                  px={6}
                  py={[4, 8]}
                  alignItems="center"
                >
                  <Flex fontSize={['sm', 'md']}>
                    <Box color="red.600" mr={4}>
                      Språk
                    </Box>
                    <Link color="red.900" fontWeight="bold" href="/" mr={4}>
                      Norsk
                    </Link>
                    <Link color="red.900" fontWeight="bold" href="/en" mr={4}>
                      English
                    </Link>
                  </Flex>
                  <Flex>
                    <a href="https://www.facebook.com/samskipnadenTromso/">
                      <Icon
                        name="nasFacebook"
                        color="red.900"
                        size="1.5rem"
                        mr={3}
                      />
                    </a>
                    <a href="https://www.instagram.com/samskipnaden_tromso/">
                      <Icon name="nasInstagram" color="red.900" size="1.5rem" />
                    </a>
                  </Flex>
                </Flex>
              </Box>
            </DrawerBody>
          </DrawerContent>
        </Drawer>
      </Flex>
    </Box>
  )
}

type Props = {
  uid: string
  headerBackgroundColor: string
  colorTheme: string
  services?: [
    {
      uid: string
      url: string
      title: string
      slug: string
    }
  ]
  submenuItems?: [MenuItemsType]
  menuItems: [MenuItemsType]
}
export default Header
